<template>
  <form novalidate >

    <h2>Billing Info</h2>

    <pickstar-loading v-if="loading" class="m-t-lg"/>

    <div v-else class="panel" >
      <div class="panel-body">

        <div class="row">

          <div class="col-md-6">
            <div class="form-group" :class="{ 'has-error' : $v.formData.full_name.$error }" >
              <label class="control-label" for="billing-name" >Full Name</label>

              <input
                id="billing-name"
                type="text"
                class="form-control"
                v-model="$v.formData.full_name.$model" />

              <vuelidate-help :field="$v.formData.full_name" label="Full Name" />

            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group">
              <label class="control-label" for="billing-company" >Company</label>

              <input
                id="billing-company"
                type="text"
                class="form-control"
                v-model="formData.company" />

            </div>
          </div>

        </div>

        <div class="row">

          <div class="col-md-6">
            <div class="form-group" :class="{ 'has-error' : $v.formData.email.$error }" >
              <label class="control-label" for="billing-email" >Email</label>

              <input
                id="billing-email"
                type="email"
                class="form-control"
                v-model="$v.formData.email.$model" />

              <vuelidate-help :field="$v.formData.email" label="Email" />

            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group" :class="{ 'has-error' : $v.formData.phone.$error }" >
              <label class="control-label" for="billing-phone" >Phone Number</label>

              <vue-tel-input
                ref="phone"
                id="billing-phone"
                :preferred-countries="['AU', 'GB', 'US']"
                :style-classes="$v.formData.phone.$error ? 'red-border form-control' : 'form-control'"
                v-model="$v.formData.phone.$model">
                <template v-slot:arrow-icon="{ open }">{{ open ? "▲" : "▼" }}</template>
              </vue-tel-input>

              <vuelidate-help :field="$v.formData.phone" label="Phone Number" />
            </div>
          </div>

        </div>

        <div class="form-group">
          <label class="control-label" for="billing-address" >Address</label>

          <input
            id="billing-address"
            type="text"
            class="form-control"
            v-model="formData.address_line_1" />

        </div>

        <div class="form-group">
          <label class="hidden" for="billing-address-2">Address Line 2</label>

          <input
            id="billing-address-2"
            type="text"
            class="form-control"
            v-model="formData.address_line_2" />
        </div>

        <div class="form-group">
          <label class="control-label" for="billing-city" >City / Town</label>

          <input
            id="billing-city"
            type="text"
            class="form-control"
            v-model="formData.city" />

        </div>

        <div class="row">

          <div class="col-sm-9">
            <div class="form-group">
              <label class="control-label" for="billing-state" >State</label>

              <input
                id="billing-state"
                type="text"
                class="form-control"
                v-model="formData.state" />

            </div>
          </div>

          <div class="col-sm-3">
            <div class="form-group">
              <label class="control-label" for="billing-zipcode" >Zip Code</label>

              <input
                id="billing-zipcode"
                type="text"
                class="form-control"
                v-model="formData.zip_code" />

            </div>
          </div>

        </div>

        <div class="form-group">
          <label class="control-label" for="billing-country" >Country</label>

          <input
            id="billing-country"
            type="text"
            class="form-control"
            v-model="formData.country" />

        </div>

      </div>

      <div class="panel-footer">
        <ladda-button
          class="btn btn-sm"
          type="submit"
          :click="close">
          Cancel
        </ladda-button>

        <ladda-button
          class="btn btn-sm btn-primary pull-right"
          type="submit"
          :click="submit">
          Save Billing Details
        </ladda-button>
      </div>
    </div>

  </form>
</template>

<script>
import { http, notify } from 'utils'
import { required, email } from 'vuelidate/lib/validators'
import VuelidateHelp from '../misc/VuelidateHelp'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  mixins: [OpportunityTracker],

  components: {
    VuelidateHelp
  },

  props: {
    user: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      loading: true,

      formData: {
        full_name: '',
        company: '',
        email: '',
        phone: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        zip_code: '',
        country: ''
      },

      countryOptions: [
        { id: 'AU', text: 'Australia' },
        { id: 'UK', text: 'United Kingdom' }
      ],

      stateOptions: [
        { id: 'NSW', text: 'New South Wales' },
        { id: 'SA', text: 'South Australia' },
        { id: 'VIC', text: 'Victoria' }
      ]
    }
  },

  async created () {
    try {
      const {data} = await http.get(`users/${this.user.id}/billing-information`)
      this.formData = data
      this.loading = false
    } catch (e) {
      this.loading = false
    }
  },

  methods: {

    async submit () {
      this.$v.$touch()

      if (this.$v.$invalid) {
        notify.error('Some fields failed validation. Please fix them and try again.')
        return
      }

      const data = { ...this.formData, phone: this.$refs.phone.phoneObject.number || '' }

      try {
        await http.post(`users/${this.user.id}/billing-information`, data)
        notify.success('Billing informations saved successfully.')
        this.trackEvent(this.analytics.V2.JobEditBillingSaveClickEvent)
        this.$emit('close')
      } catch (e) {
        notify.error('Sorry something went wrong saving your changes, please try again.')
        throw e
      }
    },

    async close () {
      this.$emit('close')
    }

  },

  validations () {
    return {
      formData: {
        full_name: {
          required
        },
        email: {
          required,
          email
        },
        phone: {
          required,
          phoneValid: () => {
            return this.$refs && this.$refs.phone && this.$refs.phone.phoneObject && this.$refs.phone.phoneObject.valid
          }
        }
      }
    }
  }

}
</script>
